class ProductSeries extends HTMLElement {
  #activeSeries = {
    key: 'all',
    label: '',
    productCollection: '',
    accessoryCollection: '',
    showAccessories: false,
    matchCollection: false,
  };

  #activeType = 'all';
  #drawerTrigger = null;
  #mediaQuery = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    if (this.hasAttribute('initialized')) return;

    this.setAttribute('initialized', '');
    this.#activeSeries.label = this.dataset.allSeriesLabel || '';
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.querySelector('[data-series-drawer]')?.addEventListener('close', this.#handleDrawerClose);
    this.#mediaQuery.addEventListener('change', this.#handleViewportChange);

    const initialFilter = this.querySelector('[data-series-filter].is-active');
    if (initialFilter instanceof HTMLButtonElement) {
      this.#activeSeries = {
        key: initialFilter.dataset.seriesKey || 'all',
        label: initialFilter.dataset.seriesLabel || this.dataset.allSeriesLabel || '',
        productCollection: initialFilter.dataset.productCollection || '',
        accessoryCollection: initialFilter.dataset.accessoryCollection || '',
        showAccessories: initialFilter.dataset.showAccessories === 'true',
        matchCollection: initialFilter.dataset.seriesMatch === 'collection',
      };
    }

    const initialType = this.querySelector('[data-series-type].is-active');
    if (initialType instanceof HTMLButtonElement) {
      this.#activeType = initialType.dataset.seriesType || 'all';
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.querySelector('[data-series-drawer]')?.removeEventListener('close', this.#handleDrawerClose);
    this.#mediaQuery.removeEventListener('change', this.#handleViewportChange);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const parentToggle = event.target.closest('[data-series-parent-toggle]');
    if (parentToggle instanceof HTMLButtonElement) {
      this.#toggleParent(parentToggle);
      return;
    }

    const filter = event.target.closest('[data-series-filter]');
    if (filter instanceof HTMLButtonElement && !filter.disabled) {
      this.#selectSeries(filter);
      return;
    }

    const type = event.target.closest('[data-series-type]');
    if (type instanceof HTMLButtonElement) {
      this.#selectType(type.dataset.seriesType || 'all');
      return;
    }

    const openButton = event.target.closest('[data-series-drawer-open]');
    if (openButton instanceof HTMLButtonElement) {
      this.#openDrawer(openButton);
      return;
    }

    if (event.target.closest('[data-series-drawer-close]')) {
      this.#closeDrawer();
      return;
    }

    const drawer = this.querySelector('[data-series-drawer]');
    if (event.target === drawer && drawer instanceof HTMLDialogElement) drawer.close();
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof HTMLButtonElement) || !event.target.matches('[data-series-type]')) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    const tabs = [...this.querySelectorAll('[data-series-type]')];
    const currentIndex = tabs.indexOf(event.target);
    let nextIndex = currentIndex;

    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;

    const nextTab = tabs[nextIndex];
    if (!(nextTab instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#selectType(nextTab.dataset.seriesType || 'all');
    nextTab.focus();
  };

  #handleDrawerClose = () => {
    const openButton = this.querySelector('[data-series-drawer-open]');
    openButton?.setAttribute('aria-expanded', 'false');
    this.#drawerTrigger?.focus();
    this.#drawerTrigger = null;
  };

  #handleViewportChange = (event) => {
    if (event.matches) this.#closeDrawer(false);
  };

  #toggleParent(button) {
    const targetId = button.getAttribute('aria-controls');
    const children = targetId ? this.querySelector(`#${CSS.escape(targetId)}`) : null;
    if (!(children instanceof HTMLElement)) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    children.hidden = expanded;
  }

  #selectSeries(button) {
    this.#activeSeries = {
      key: button.dataset.seriesKey || 'all',
      label: button.dataset.seriesLabel || this.dataset.allSeriesLabel || '',
      productCollection: button.dataset.productCollection || '',
      accessoryCollection: button.dataset.accessoryCollection || '',
      showAccessories: button.dataset.showAccessories === 'true',
      matchCollection: button.dataset.seriesMatch === 'collection',
    };

    this.querySelectorAll('[data-series-filter]').forEach((item) => {
      const active = item.dataset.seriesKey === this.#activeSeries.key;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
      if (active) this.#revealFilter(item);
    });

    this.querySelectorAll('[data-current-series]').forEach((label) => {
      label.textContent = this.#activeSeries.label;
    });

    this.#render();
    if (button.closest('[data-series-drawer]')) this.#closeDrawer();
  }

  #selectType(type) {
    this.#activeType = type;
    this.querySelectorAll('[data-series-type]').forEach((button) => {
      const active = button.dataset.seriesType === type;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    this.#render();
  }

  #revealFilter(filter) {
    let children = filter.closest('.product-series-directory__children');
    while (children instanceof HTMLElement) {
      children.hidden = false;
      const toggle = this.querySelector(`[aria-controls="${CSS.escape(children.id)}"]`);
      toggle?.setAttribute('aria-expanded', 'true');
      children = children.parentElement?.closest('.product-series-directory__children') || null;
    }
  }

  #openDrawer(button) {
    const drawer = this.querySelector('[data-series-drawer]');
    if (!(drawer instanceof HTMLDialogElement)) return;

    this.#drawerTrigger = button;
    button.setAttribute('aria-expanded', 'true');
    drawer.showModal();
    drawer.querySelector('[data-series-drawer-close]')?.focus();
  }

  #closeDrawer(restoreFocus = true) {
    const drawer = this.querySelector('[data-series-drawer]');
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;

    if (!restoreFocus) this.#drawerTrigger = null;
    drawer.close();
  }

  #matchesSeries(card) {
    if (this.#activeSeries.key === 'all') return true;

    return this.#matchesSeriesData(card, this.#activeSeries);
  }

  #matchesSeriesData(card, series) {
    if (card.dataset.itemType === 'accessory' && !series.matchCollection) {
      if (!series.showAccessories) return false;
      return this.#hasToken(card.dataset.collectionTitleKeys, series.accessoryCollection);
    }

    return this.#hasToken(card.dataset.collectionHandles, series.productCollection);
  }

  #hasToken(value, token) {
    return Boolean(token) && (value || '').includes(`|${token}|`);
  }

  #render() {
    const grid = this.querySelector('[data-testid="product-grid"]');
    if (!(grid instanceof HTMLElement)) return;

    const cards = [...grid.querySelectorAll(':scope > [data-series-product]')];
    let visibleCount = 0;

    cards.forEach((card) => {
      const matchesType = this.#activeType === 'all' || card.dataset.itemType === this.#activeType;
      const visible = matchesType && this.#matchesSeries(card);
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    const countTemplate = this.dataset.countTemplate || '__count__ products';
    this.querySelectorAll('[data-series-count]').forEach((count) => {
      count.textContent = countTemplate.replace('__count__', String(visibleCount));
    });

    const emptyState = this.querySelector('[data-series-empty]');
    if (emptyState instanceof HTMLElement) emptyState.hidden = visibleCount !== 0;
  }
}

if (!customElements.get('product-series')) {
  customElements.define('product-series', ProductSeries);
}
